import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  CheckCheck,
  Bell,
  Heart,
  MessageSquare,
  Repeat2,
  UserPlus,
  Eye,
  Loader2,
} from "lucide-react";
import api, { unwrap, apiError } from "@/lib/api";
import { EP } from "@/lib/endpoints";
import UserAvatar from "@/components/UserAvatar";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * Notifications — 1:1 web port of iOS `src/app/(app)/notifications.tsx`.
 *
 * Layout:
 *   • Header: back chevron + "Notifications" title + `CheckCheck` mark-all pill
 *     (disabled/muted when the list is empty).
 *   • FlashList body of rows.  Each row shows:
 *       [ 44px UserAvatar with 22px type-icon badge overlapping bottom-right ]
 *       [ bold actor name + action sentence + timestamp ]
 *       [ 8px brand dot when unread ]
 *   • Unread rows: `theme.brandSoft` background.  Read rows: `theme.background`.
 *   • Empty state: Bell icon, "You're all caught up.", subtitle.
 *
 * Type classification is substring-based on lowercased `type`/`event_type`,
 * exactly mirroring `iconForType` + `labelForType` in the iOS source.
 * Deletion is local-only (Xano has no DELETE endpoint) so a "Dismiss"
 * button hides the row from the current session; the id is remembered in
 * localStorage under `brontoy_deleted_notifications_v1` (cap 500 ids).
 */

const BRAND = "#7638FA";
const BRAND_SOFT = "rgba(118, 56, 250, 0.08)";
const DELETE_STORAGE_KEY = "brontoy_deleted_notifications_v1";
const PER_PAGE = 20;

function loadDeleted() {
  try {
    const raw = localStorage.getItem(DELETE_STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.map(String) : [];
  } catch {
    return [];
  }
}
function persistDeleted(ids) {
  try {
    // Cap at 500 to keep the list small.
    const capped = ids.slice(-500);
    localStorage.setItem(DELETE_STORAGE_KEY, JSON.stringify(capped));
  } catch {
    /* ignore quota */
  }
}

function relTime(input) {
  if (!input) return "";
  const d = new Date(input);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (Number.isNaN(diff)) return "";
  if (diff < 60) return "now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function iconForType(t = "") {
  const lower = String(t).toLowerCase();
  if (lower.includes("agree") || lower.includes("like")) return Heart;
  if (lower.includes("reply") || lower.includes("comment") || lower.includes("mention"))
    return MessageSquare;
  if (lower.includes("repost")) return Repeat2;
  if (lower.includes("follow")) return UserPlus;
  if (lower.includes("different")) return Eye;
  return Bell;
}

function labelForType(n) {
  const t = String(n.type || n.event_type || "").toLowerCase();
  const actor = n.actor || n.from_user || {};
  const name = actor.display_name || actor.name || actor.username || "Someone";
  const body =
    n.comment_text ||
    n.comment ||
    n.reply_text ||
    n.reply ||
    n.content ||
    n.preview ||
    "";
  const trim = (s) => (s.length > 80 ? s.slice(0, 77) + "…" : s);
  if (t.includes("agree")) return { name, rest: " agreed with your post." };
  if (t.includes("like")) return { name, rest: " liked your post." };
  if (t.includes("reply") || t.includes("comment")) {
    return body
      ? { name, rest: ` commented: "${trim(body)}"` }
      : { name, rest: " commented on your post." };
  }
  if (t.includes("mention")) return { name, rest: " mentioned you." };
  if (t.includes("repost")) return { name, rest: " reposted your post." };
  if (t.includes("follow_request") || t.includes("request"))
    return { name, rest: " requested to follow you." };
  if (t.includes("follow")) return { name, rest: " started following you." };
  if (t.includes("different"))
    return { name, rest: " shared a different view on your opinion." };
  return { name, rest: n.message ? ` ${n.message}` : " interacted with your content." };
}

function destinationFor(n) {
  const actor = n.actor || n.from_user || {};
  const t = String(n.type || n.event_type || "").toLowerCase();
  const postId = n.post_id ?? n.target_id;
  if ((t.includes("follow") || t.includes("request")) && actor.username) {
    return `/${actor.username}`;
  }
  if (postId != null) return `/post/${postId}`;
  if (actor.username) return `/${actor.username}`;
  return null;
}

function Row({ n, onOpen, onDismiss }) {
  const actor = n.actor || n.from_user || {};
  const id = n.id ?? n.notification_id;
  const read = !!(n.read || n.is_read);
  const Icon = iconForType(n.type || n.event_type);
  const { name, rest } = labelForType(n);
  const time = relTime(n.created_at || n.createdAt);

  return (
    <div
      className="relative"
      style={{ backgroundColor: read ? "transparent" : BRAND_SOFT }}
      data-testid={`notif-item-${id}`}
    >
      <button
        type="button"
        onClick={() => onOpen(n)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-secondary/60 active:bg-secondary transition-colors border-b border-border"
      >
        {/* Avatar + type badge */}
        <div className="relative shrink-0">
          <UserAvatar user={actor} size="md" />
          <span
            className="absolute -bottom-0.5 -right-0.5 w-[22px] h-[22px] rounded-full flex items-center justify-center"
            style={{
              backgroundColor: "hsl(var(--background))",
              borderWidth: 1,
              borderColor: "hsl(var(--border))",
            }}
          >
            <Icon size={12} style={{ color: BRAND }} strokeWidth={2.2} />
          </span>
        </div>

        {/* Text */}
        <div className="flex-1 min-w-0">
          <p className="text-foreground text-[14.5px] leading-5">
            <span className="font-bold">{name}</span>
            <span>{rest}</span>
          </p>
          {time && (
            <p className="text-muted-foreground text-[12px] mt-0.5">{time}</p>
          )}
        </div>

        {/* Unread dot */}
        {!read && (
          <span
            className="w-2 h-2 rounded-full shrink-0"
            style={{ background: BRAND }}
            aria-label="Unread"
          />
        )}
      </button>

      {/* Dismiss (local-only delete) */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDismiss(String(id));
        }}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] uppercase font-bold tracking-wider text-muted-foreground hover:text-destructive px-2 py-1 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
        aria-label="Dismiss"
      >
        Dismiss
      </button>
    </div>
  );
}

export default function Notifications() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [err, setErr] = useState("");
  const [markingAll, setMarkingAll] = useState(false);
  const [deletedIds, setDeletedIds] = useState(loadDeleted);

  const normalize = (raw) => {
    const d = unwrap(raw);
    const list = Array.isArray(d)
      ? d
      : d?.items || d?.notifications || d?.data || [];
    const hasMoreField =
      d?.next_page != null ? true : d?.has_more != null ? !!d.has_more : list.length >= PER_PAGE;
    return { items: list, hasMore: hasMoreField };
  };

  const idOf = (n) => String(n.id ?? n.notification_id);

  const loadPage = useCallback(
    async (p) => {
      const target = p === 1 ? setLoading : setLoadingMore;
      target(true);
      setErr("");
      try {
        const res = await api.get(EP.notifications(p, PER_PAGE));
        const { items: fresh, hasMore: more } = normalize(res.data);
        const filtered = fresh.filter((n) => !deletedIds.includes(idOf(n)));
        setItems((prev) => (p === 1 ? filtered : [...prev, ...filtered]));
        setHasMore(more);
        setPage(p);
      } catch (e) {
        setErr(apiError(e));
      } finally {
        target(false);
      }
    },
    [deletedIds]
  );

  useEffect(() => {
    loadPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markOne = async (n) => {
    const id = idOf(n);
    // Optimistic update
    setItems((prev) =>
      prev.map((x) => (idOf(x) === id ? { ...x, read: true, is_read: true } : x))
    );
    try {
      await api.patch(EP.readNotification(n.id ?? n.notification_id), {});
    } catch {
      /* silent — cached UI wins */
    }
  };

  const markAll = async () => {
    if (items.length === 0 || markingAll) return;
    setMarkingAll(true);
    try {
      await api.patch(EP.readAllNotifications, {});
      setItems((prev) => prev.map((x) => ({ ...x, read: true, is_read: true })));
      toast.success("All caught up.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setMarkingAll(false);
    }
  };

  const openOne = async (n) => {
    await markOne(n);
    const dest = destinationFor(n);
    if (dest) navigate(dest);
  };

  const dismissOne = (id) => {
    const next = [...deletedIds, id];
    setDeletedIds(next);
    persistDeleted(next);
    setItems((prev) => prev.filter((n) => idOf(n) !== id));
    // Best-effort mark read too
    api.patch(EP.readNotification(id), {}).catch(() => {});
  };

  const back = () => (window.history.length > 1 ? navigate(-1) : navigate("/home", { replace: true }));

  const empty = !loading && items.length === 0 && !err;

  return (
    <div className="min-h-[100dvh] w-full bg-background text-foreground flex flex-col" data-testid="notifications-screen">
      {/* Header */}
      <div
        className="h-[52px] px-2 flex items-center justify-between border-b border-border shrink-0"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <button
          type="button"
          onClick={back}
          className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-secondary transition-colors"
          aria-label="Back"
          data-testid="notif-back"
        >
          <ChevronLeft size={25} strokeWidth={2.4} />
        </button>
        <h1 className="text-foreground font-jakarta font-bold text-[17px]" style={{ letterSpacing: "-0.3px" }}>
          Notifications
        </h1>
        <button
          type="button"
          onClick={markAll}
          disabled={items.length === 0 || markingAll}
          className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-secondary transition-colors disabled:cursor-not-allowed"
          aria-label="Mark all as read"
          data-testid="notif-mark-all"
        >
          {markingAll ? (
            <Loader2 className="w-4 h-4 animate-spin" style={{ color: BRAND }} />
          ) : (
            <CheckCheck
              size={22}
              strokeWidth={2}
              style={{ color: items.length === 0 ? "hsl(var(--muted-foreground))" : BRAND }}
            />
          )}
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto pb-8">
        {loading ? (
          <div className="pt-8 flex justify-center" data-testid="notif-loading">
            <Loader2 className="w-5 h-5 animate-spin" style={{ color: BRAND }} />
          </div>
        ) : err ? (
          <div className="p-6">
            <div
              className="rounded-2xl p-3.5"
              style={{ backgroundColor: "rgba(255, 59, 48, 0.13)" }}
            >
              <p className="text-[#FF3B30] text-sm">{err}</p>
            </div>
          </div>
        ) : empty ? (
          <div className="pt-14 flex flex-col items-center text-center gap-3 px-6" data-testid="notif-empty">
            <Bell size={30} className="text-muted-foreground" strokeWidth={1.8} />
            <h3 className="text-foreground font-bold text-[15px] mt-1">
              You&apos;re all caught up.
            </h3>
            <p className="text-muted-foreground text-[13px] max-w-[260px]">
              Likes, replies, and follows will show up here.
            </p>
          </div>
        ) : (
          <div className="group divide-y divide-border">
            {items.map((n) => (
              <Row
                key={idOf(n)}
                n={n}
                onOpen={openOne}
                onDismiss={dismissOne}
              />
            ))}
            {hasMore && (
              <div className="flex justify-center py-4">
                <button
                  type="button"
                  onClick={() => loadPage(page + 1)}
                  disabled={loadingMore}
                  className="h-9 px-4 rounded-full bg-secondary text-foreground font-bold text-[13px] hover:opacity-90 disabled:opacity-60"
                  data-testid="notif-load-more"
                >
                  {loadingMore ? (
                    <Loader2 className="w-4 h-4 animate-spin" style={{ color: BRAND }} />
                  ) : (
                    "Load more"
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
